import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { InboxIcon, MailIcon, HardDriveIcon, FileTextIcon, FolderLockIcon, ClipboardListIcon, CheckIcon, MinusIcon, LoaderIcon, ThumbsUpIcon, MessageSquareIcon, RadarIcon } from 'lucide-react';
import { DEMO_DEAL, DEMO_RADAR, DEMO_REPORTS } from '../../../data/dgpt';
import { FitScoreRing } from './FitScoreRing';
import { cn } from '../../../src/lib/utils';
type StepId = 'capture' | 'match' | 'case' | 'collaborate' | 'radar';
const CHANNELS = [{
  icon: InboxIcon,
  label: 'Founder portal',
  count: 14
}, {
  icon: MailIcon,
  label: 'Email forwarding',
  count: 9
}, {
  icon: HardDriveIcon,
  label: 'Google Drive',
  count: 6
}, {
  icon: FileTextIcon,
  label: 'DocSend',
  count: 4
}, {
  icon: FolderLockIcon,
  label: 'Data rooms',
  count: 7
}, {
  icon: ClipboardListIcon,
  label: 'Applications',
  count: 22
}];
const FUNDS = [{
  name: 'Healthcare Fund',
  deals: 47,
  active: true
}, {
  name: 'Tech Fund',
  deals: 31
}, {
  name: 'Entertainment Fund',
  deals: 18
}, {
  name: 'B2B Software',
  deals: 24
}];
function Panel({
  title,
  meta,
  children
}: {
  title: string;
  meta?: string;
  children: React.ReactNode;
}) {
  return <div className="flex h-full flex-col rounded-2xl border border-hairline bg-panel" data-id="element-265" data-mp="1zm6bwtt4gwrq">
      <div className="flex items-center justify-between border-b border-hairline px-4 py-3" data-id="element-266" data-mp="24khq1gqg9tfr">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-secondary" data-id="element-267" data-mp="1ql89gfyc0xb9">{title}</span>
        {meta ? <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-text-tertiary" data-id="element-268" data-mp="1unmosq2slexs">
            <span className="h-1.5 w-1.5 rounded-full bg-lime" aria-hidden="true" data-id="element-269" data-mp="1gon2vq6nze5b" />
            {meta}
          </span> : null}
      </div>
      <div className="flex-1 p-4" data-id="element-270" data-mp="3b137kmjtak82">{children}</div>
    </div>;
}
export function StepVisual({
  step
}: {
  step: StepId;
}) {
  const reduceMotion = useReducedMotion();
  const enter = (index: number) => reduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 10
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.4,
      delay: index * 0.07,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
    }
  };
  if (step === 'capture') {
    return <Panel title="Inbox · Every channel" meta="Ingesting" data-id="element-271" data-mp="2zlqdwvd8qhqk">
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-272" data-mp="2znl00d090fw8">
          {CHANNELS.map((channel, index) => <motion.li key={channel.label} {...enter(index)} className="flex items-center justify-between rounded-xl border border-hairline-soft bg-elevated px-3 py-3" data-id="element-273" data-mp="15xjga85y0he" data-mpi={index}>
              <span className="flex items-center gap-2.5 text-xs text-text-primary" data-id="element-274" data-mp="3ikcf0a8rc8uh">
                <channel.icon className="h-4 w-4 text-text-tertiary" aria-hidden="true" data-id="element-275" data-mp="3iii3tnnafv3l" />
                {channel.label}
              </span>
              <span className="font-mono text-[11px] text-lime" data-id="element-276" data-mp="f3dopieq3o7k">+{channel.count}</span>
            </motion.li>)}
        </ul>
        <motion.div {...enter(6)} className="mt-3 rounded-xl border border-lime/25 bg-lime/[0.06] px-3 py-3" data-id="element-277" data-mp="ne2zpx6zp65h">
          <p className="text-xs text-text-primary" data-id="element-278" data-mp="rfy08zh3jfi7">
            {DEMO_DEAL.name} <span className="text-text-tertiary" data-id="element-279" data-mp="wc6m0vpsu6yc">· {DEMO_DEAL.sector}</span>
          </p>
          <p className="mt-1 text-[11px] text-text-secondary" data-id="element-280" data-mp="11d2lmx34b6xy">5 files ingested · pitch deck, financials, cap table</p>
        </motion.div>
      </Panel>;
  }
  if (step === 'match') {
    return <Panel title={`Thesis fit · ${DEMO_DEAL.name}`} meta="Healthcare Fund" data-id="element-281" data-mp="1abo46s8lalyq">
        <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-start" data-id="element-282" data-mp="1f2xsnpiejj91">
          <FitScoreRing score={DEMO_DEAL.fitScore} runKey={step} data-id="element-283" data-mp="1k2990xeuwmq3" />
          <ul className="flex w-full flex-col gap-2" data-id="element-284" data-mp="1k4n250pz7ov0">
            {DEMO_DEAL.criteria.map((criterion, index) => <motion.li key={criterion.label} {...enter(index)} className="flex items-center justify-between gap-2 rounded-lg border border-hairline-soft bg-elevated px-3 py-2.5" data-id="element-285" data-mp="1u0q3i4d07jgw" data-mpi={index}>
                <span className="text-xs text-text-primary" data-id="element-286" data-mp="2lrz7yp207zt9">{criterion.label}</span>
                {criterion.met ? <CheckIcon className="h-4 w-4 text-lime" aria-label="Criterion met" data-id="element-287" data-mp="30hdc9iqq3r1s" /> : <span className="inline-flex items-center gap-1 rounded-md border border-[#F0E0C4] bg-[#FDF7ED] px-1.5 py-0.5 font-mono text-[11px] text-[#92400E]" data-id="element-288" data-mp="2vkwsf0qk3yui">
                    <MinusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-289" data-mp="22v698da2i6rj" />
                    {'value' in criterion ? criterion.value : ''}
                  </span>}
              </motion.li>)}
          </ul>
        </div>
        <p className="mt-4 text-[11px] text-text-tertiary" data-id="element-290" data-mp="2cuoqglylfefw">Strong match · Healthcare AI thesis</p>
      </Panel>;
  }
  if (step === 'case') {
    return <Panel title="Investment case" meta="PDF export" data-id="element-291" data-mp="3o6xzwextnche">
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-292" data-mp="3vra2imr4hppj">
          {DEMO_REPORTS.map((report, index) => {
          const generating = index >= 6;
          return <motion.li key={report} {...enter(index)} className="flex items-center justify-between rounded-lg border border-hairline-soft bg-elevated px-3 py-2.5" data-id="element-293" data-mp="4imdnwtobkd8" data-mpi={index}>
                <span className="flex items-center gap-2 text-xs text-text-primary" data-id="element-294" data-mp="icdl8znugu6b">
                  <FileTextIcon className="h-3.5 w-3.5 text-text-tertiary" aria-hidden="true" data-id="element-295" data-mp="nb71gzuimza6" />
                  {report}
                </span>
                {generating ? <span className="inline-flex items-center gap-1 rounded-md border border-[#F0E0C4] bg-[#FDF7ED] px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[#92400E]" data-id="element-296" data-mp="8k7wrzwswuvv">
                    <LoaderIcon className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" data-id="element-297" data-mp="119eskr4vf6df" />
                    Generating
                  </span> : <span className="text-[10px] uppercase tracking-wider text-lime" data-id="element-298" data-mp="1fx54p1vsee3n">Ready</span>}
              </motion.li>;
        })}
        </ul>
      </Panel>;
  }
  if (step === 'collaborate') {
    return <Panel title="Workspaces · Team review" meta="IC Thursday" data-id="element-299" data-mp="11xw2550i7ii5">
        <ul className="flex flex-col gap-2" data-id="element-300" data-mp="11xm918k5wv88">
          {FUNDS.map((fund, index) => <motion.li key={fund.name} {...enter(index)} className={cn('flex items-center justify-between rounded-lg border px-3 py-2.5', fund.active ? 'border-lime/30 bg-lime/[0.06]' : 'border-hairline-soft bg-elevated')} data-id="element-301" data-mp="x0dpb3jd8j4v" data-mpi={index}>
              <span className="text-xs text-text-primary" data-id="element-302" data-mp="j5l2dk15c0o9">{fund.name}</span>
              <span className="font-mono text-[11px] text-text-secondary" data-id="element-303" data-mp="e7rzp0z1xosq">{fund.deals} deals</span>
            </motion.li>)}
        </ul>
        <motion.div {...enter(4)} className="mt-3 grid grid-cols-3 gap-2" data-id="element-304" data-mp="2kwtwjk2pe65d">
          {[{
          label: 'Votes',
          value: '3 / 4',
          icon: ThumbsUpIcon
        }, {
          label: 'Comments',
          value: '12',
          icon: MessageSquareIcon
        }, {
          label: 'Stage',
          value: 'IC prep',
          icon: ClipboardListIcon
        }].map(stat => <div key={stat.label} className="rounded-lg border border-hairline-soft bg-elevated px-3 py-2.5" data-id="element-305" data-mp="2yv1pzi0j6pbs">
              <stat.icon className="h-3.5 w-3.5 text-text-tertiary" aria-hidden="true" data-id="element-306" data-mp="227napo6m4yfc" />
              <p className="mt-1.5 font-mono text-sm text-text-primary" data-id="element-307" data-mp="275ykjywwr8xk">{stat.value}</p>
              <p className="text-[10px] uppercase tracking-wider text-text-tertiary" data-id="element-308" data-mp="2b5pjwr0i2xdp">{stat.label}</p>
            </div>)}
        </motion.div>
      </Panel>;
  }
  return <Panel title="Deal Radar" meta="12 new this week" data-id="element-309" data-mp="1jg4blsfpm4il">
      <div className="mb-3 flex items-center gap-2 text-[11px] text-text-secondary" data-id="element-310" data-mp="1jdi3nrowpmzu">
        <RadarIcon className="h-4 w-4 text-lime" aria-hidden="true" data-id="element-311" data-mp="1nfmqo7kunldi" />
        Matched to Healthcare Fund thesis
      </div>
      <ul className="flex flex-col gap-2" data-id="element-312" data-mp="gvos5lgi37yf">
        {DEMO_RADAR.map((company, index) => <motion.li key={company.name} {...reduceMotion ? {} : {
        initial: {
          opacity: 0,
          x: 16
        },
        animate: {
          opacity: 1,
          x: 0
        },
        transition: {
          duration: 0.45,
          delay: index * 0.12,
          ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
        }
      }} className="flex items-center justify-between gap-3 rounded-lg border border-hairline-soft bg-elevated px-3 py-3" data-id="element-313" data-mp="bwvftwcy9mzu" data-mpi={index}>
            <span className="min-w-0" data-id="element-314" data-mp="19kdjwtvo20xi">
              <span className="block truncate text-xs font-medium text-text-primary" data-id="element-315" data-mp="14j6cwx1cl5rw">{company.name}</span>
              <span className="block truncate text-[11px] text-text-tertiary" data-id="element-316" data-mp="1ik8zrc062k3v">{company.meta}</span>
            </span>
            <span className="flex items-center gap-2" data-id="element-317" data-mp="1ej6yyqqlpzzs">
              <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-hairline-soft sm:block" data-id="element-318" data-mp="1x664u6ck55lk">
                <span className="block h-full rounded-full bg-lime" style={{
              width: `${company.score}%`
            }} data-id="element-319" data-mp="225iz3c5rdey6" />
              </span>
              <span className="font-mono text-xs text-lime" data-id="element-320" data-mp="1o4gbuw311ylo">{company.score}%</span>
            </span>
          </motion.li>)}
      </ul>
    </Panel>;
}
export type { StepId };